import { useState } from "react";

interface DirectLoginProps {
  onSuccess: (token: string) => void;
}

const SSO_CLIENT_ID = "we9dvc146c3dRFSS248d";
const API_BASE = "/api/auth";

const DirectLogin = ({ onSuccess }: DirectLoginProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!username || !password) {
      setError("Enter your username/email and password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Login to get access_token
      const loginRes = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username,
          password: password,
          type: "email",
        }),
      });
      const loginData = await loginRes.json();

      if (!loginData.status) {
        setError(loginData.message || "Login failed");
        setLoading(false);
        return;
      }

      const accessToken = loginData.result?.access_token;
      if (!accessToken) {
        setError("Login succeeded but no token received");
        setLoading(false);
        return;
      }

      // Step 2: Get SSO authorization code
      const authRes = await fetch(`${API_BASE}/sso-authorize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: SSO_CLIENT_ID,
          redirect_uri: window.location.origin,
        }),
      });
      const authData = await authRes.json();

      if (!authData.status) {
        setError("SSO authorization failed");
        setLoading(false);
        return;
      }

      // Step 3: Exchange for final token with user info
      const tokenRes = await fetch(`${API_BASE}/sso-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          authorization_code: authData.token,
          access_token: accessToken,
        }),
      });
      const tokenData = await tokenRes.json();

      if (!tokenData.status || !tokenData.token) {
        setError("Token exchange failed");
        setLoading(false);
        return;
      }

      // Success - pass the final JWT to parent
      onSuccess(tokenData.token);
    } catch (err: any) {
      setError("Connection error. Please try again.");
    }

    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div style={{ width: "100%", maxWidth: "280px", margin: "0 auto" }}>
      <input
        type="text"
        placeholder="Username or email"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          padding: "10px 14px",
          borderRadius: "6px",
          border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(255,255,255,0.1)",
          color: "white",
          fontSize: "14px",
          marginBottom: "8px",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          width: "100%",
          padding: "10px 14px",
          borderRadius: "6px",
          border: "1px solid rgba(255,255,255,0.2)",
          background: "rgba(255,255,255,0.1)",
          color: "white",
          fontSize: "14px",
          marginBottom: "10px",
          outline: "none",
          boxSizing: "border-box",
        }}
      />
      {error && (
        <p style={{ color: "#ff5252", fontSize: "12px", margin: "0 0 8px", textAlign: "center" }}>
          {error}
        </p>
      )}
      <button
        onClick={handleLogin}
        disabled={loading}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: "6px",
          border: "none",
          background: loading ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.2)",
          color: "white",
          fontSize: "14px",
          fontWeight: "bold",
          cursor: loading ? "wait" : "pointer",
          transition: "background 0.2s",
        }}
        onMouseOver={(e) => !loading && (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
        onMouseOut={(e) => !loading && (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
      >
        {loading ? "Signing in..." : "Sign In"}
      </button>
      <p style={{ fontSize: "11px", opacity: 0.4, textAlign: "center", marginTop: "8px", marginBottom: 0 }}>
        Don't have an account?{" "}
        <a href="https://pentagon.games/sign-up" target="_blank" rel="noreferrer" style={{ color: "white", textDecoration: "underline" }}>
          Sign up
        </a>
      </p>
    </div>
  );
};

export default DirectLogin;
